import { dbAdd, dbClear, dbDelete, dbGet, dbGetAll, dbPut, QUEUE_STORE } from "./db";

export type QueuedActionType =
  | "MARK_POINT_VISITED"
  | "UNMARK_POINT_VISITED"
  | "SUBMIT_VISIT_REPORT"
  | "UPDATE_HOUSEHOLD_CONTACT";

export interface QueuedRow {
  id: number;
  type: QueuedActionType;
  /** What the UI badge looks itself up by, e.g. "point:41". */
  entityKey: string;
  payload: Record<string, unknown>;
  idempotencyKey: string;
  createdAt: number;
  retryCount: number;
  status: "pending" | "failed";
  lastError?: string;
}

export type NewQueuedRow = Omit<QueuedRow, "id" | "createdAt" | "retryCount" | "status">;

const byId = (a: QueuedRow, b: QueuedRow) => a.id - b.id;

export const enqueue = async (row: NewQueuedRow): Promise<QueuedRow> => {
  const value = {
    ...row,
    createdAt: Date.now(),
    retryCount: 0,
    status: "pending" as const,
  };
  const id = await dbAdd(QUEUE_STORE, value);
  return { ...value, id };
};

export const listQueue = async (): Promise<QueuedRow[]> =>
  (await dbGetAll<QueuedRow>(QUEUE_STORE)).sort(byId);

export const listPending = async (): Promise<QueuedRow[]> =>
  (await listQueue()).filter((r) => r.status === "pending");

export const removeFromQueue = (id: number): Promise<void> => dbDelete(QUEUE_STORE, id);

const patch = async (id: number, changes: Partial<QueuedRow>): Promise<QueuedRow | undefined> => {
  const existing = await dbGet<QueuedRow>(QUEUE_STORE, id);
  if (!existing) return undefined;
  const updated = { ...existing, ...changes };
  await dbPut(QUEUE_STORE, updated);
  return updated;
};

export const markFailed = async (id: number, error: string): Promise<void> => {
  await patch(id, { status: "failed", lastError: error });
};

export const markPending = async (id: number): Promise<void> => {
  await patch(id, { status: "pending", lastError: undefined });
};

export const bumpRetry = async (id: number): Promise<number> => {
  const existing = await dbGet<QueuedRow>(QUEUE_STORE, id);
  if (!existing) return 0;
  const updated = await patch(id, { retryCount: existing.retryCount + 1 });
  return updated ? updated.retryCount : existing.retryCount;
};

export const clearQueue = (): Promise<void> => dbClear(QUEUE_STORE);
